import { Box } from '@breathelife/mui';
import { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ESignCeremonyStatus, Permission } from '@breathelife/types';

import { SubmitButton } from '../../../Components/Button/SubmitButton';
import { RestrictedToUserPermissions } from '../../../Components/Restricted/RestrictedToUserPermissions';
import Typography from '../../../Components/Typography';
import { CarrierContext } from '../../../Context/CarrierContext';
import { ModalLayout } from '../../../Layouts/Modal/ModalLayout';
import { Lead } from '../../../Models/Lead';
import { useNavigation } from '../../../Hooks';
import { generateLeadsListUrl } from '../../../Navigation/Urls';
import { useDeleteLeadMutation } from '../../../ReactQuery/Lead/lead.mutations';
import { useNavigate } from 'react-router-dom';

export type Props = {
  isOpen: boolean;
  closeModal: () => void;
  lead: Lead;
};

export function DeleteLeadModal(props: Props): ReactElement {
  const { t } = useTranslation();
  const { features } = useContext(CarrierContext);
  const { isOpen, closeModal, lead } = props;
  const { leadTab } = useNavigation();
  const navigate = useNavigate();

  const deleteLeadMutation = useDeleteLeadMutation({
    onSuccess: () => {
      navigate(generateLeadsListUrl(leadTab));
      closeModal();
    },
  });

  function deleteLead(): void {
    deleteLeadMutation.mutate(lead.id);
  }

  const activeESignCeremonyStatuses = [
    ESignCeremonyStatus.IN_PROGRESS,
    ESignCeremonyStatus.READY,
    ESignCeremonyStatus.SENT,
  ];

  const isSignatureCeremonyInProgress = lead?.applications?.[0]?.eSignCeremonies.some((ceremony) =>
    activeESignCeremonyStatuses.includes(ceremony.status),
  );

  return (
    <ModalLayout
      maxWidth='sm'
      isOpen={isOpen}
      closeModal={closeModal}
      title={t(
        features.leadCreation.enabled
          ? 'modals.deleteLead.deleteLeadTitle'
          : 'modals.deleteLead.deleteApplicationTitle',
      )}
      submitButton={
        <RestrictedToUserPermissions requiredPermissions={[Permission.LeadDelete]}>
          <SubmitButton
            isLoading={deleteLeadMutation.isLoading}
            disabled={deleteLeadMutation.isLoading || isSignatureCeremonyInProgress}
            onClick={deleteLead}
            data-testid='lead-delete-confirm'
          >
            {t('cta.delete')}
          </SubmitButton>
        </RestrictedToUserPermissions>
      }
    >
      <Box mb={5}>
        <Typography variant='body1' grey={90}>
          {t(
            features.leadCreation.enabled
              ? 'modals.deleteLead.deleteLeadConfirmation'
              : 'modals.deleteLead.deleteApplicationConfirmation',
          )}{' '}
          {t('modals.deleteLead.irreversible')}
        </Typography>
      </Box>
    </ModalLayout>
  );
}
